import { useMemo } from 'react';

import { FormProvider } from 'core/states';
import { DiscountApplyToTypes } from 'corporate/enums';
import { getDefaultValues } from 'pricing/components';
import { useCostingCalculationActions } from 'pricing/hooks';
import { IPricingProfile, IPricingProfileCostingCalculation } from 'pricing/types';

import { upsertPricingProfileCostingCalculationSchema } from './schema';
import { IPricingProfileCostingCalculationForm } from './types';
import { UpsertPricingProfileCostingCalculationFormFields } from './UpsertPricingProfileCostingCalculationFormFields';
import { mapUpsertPricingProfileCostingCalculation } from './utils';

interface IProps {
  pricingProfile: IPricingProfile;
  pricingProfileCostingCalculation?: IPricingProfileCostingCalculation;
  isOpen: boolean;
  onClose: () => void;
  onSubmitSuccess: () => void;
  onRemoveSuccess?: () => void;
}

export const UpsertPricingProfileCostingCalculation = ({
  pricingProfile,
  pricingProfileCostingCalculation,
  isOpen,
  onClose,
  onSubmitSuccess,
  onRemoveSuccess,
}: IProps) => {
  const { upsertCostingCalculation } = useCostingCalculationActions();

  const defaultValues = useMemo(
    () => ({
      ...getDefaultValues(pricingProfileCostingCalculation),
      applyTo: pricingProfileCostingCalculation?.applyTo || DiscountApplyToTypes.STD_COST,
    }),
    [pricingProfileCostingCalculation]
  );

  const onSubmit = () => async (data: IPricingProfileCostingCalculationForm) => {
    const { success } = await upsertCostingCalculation(
      mapUpsertPricingProfileCostingCalculation(
        pricingProfile.costDiscountStructureId,
        data,
        pricingProfileCostingCalculation?.id
      )
    );

    if (!success) {
      return;
    }

    setTimeout(() => {
      onSubmitSuccess();
      onClose();
    }, 1000);
  };

  const handleOnRemoveSuccess = () => {
    setTimeout(() => {
      onRemoveSuccess?.();
      onClose();
    }, 1000);
  };

  return (
    <FormProvider
      schema={upsertPricingProfileCostingCalculationSchema()}
      defaultValues={defaultValues}
      resetDefaultValues
    >
      <UpsertPricingProfileCostingCalculationFormFields
        pricingProfileCostingCalculation={pricingProfileCostingCalculation}
        isOpen={isOpen}
        onClose={onClose}
        onSubmit={onSubmit}
        onRemoveSuccess={handleOnRemoveSuccess}
      />
    </FormProvider>
  );
};
